// Procedural texture atlas (client — Canvas 2D, no external assets, ARCH §D3/§11).
// Ported from the legacy `makeAtlas`; tile ids come from the sim's atlasmap so the
// mesher's UVs and these painted pixels agree.
import * as THREE from 'three';
import { TILE, TILE_PX, ATLAS_TILES, EXTRA_TILES, VEG, M4T, NETHER, ATLAS_BLK, M9T, M11T, M13T, M18T, M19T, M19B, M20T, M20B, M40T, M41T, M42T, M44T, M45T, M46T, M47T, M48T, M49T, M50T, M51T, M52T, M53T, M54T, M55T, M56T, M57T, M59T, M60T, M61T, FLOWERS, flowerTileOf, M21T, M22T, M22B, M22C, MB2T, MB5T, MB6T, MB7T, MB8T, MB9T, MB10T, MB11T, FORGE, SCRIPT_TILE, WOOD_SPECIES, WOOD_TILES, DYE_ORDER, FAMILY_ORDER, familyTileOf, glazedTerracottaTileOf, type WoodSpecies } from '../../../sim/mesh/atlasmap';
import { DEFAULT_FORGE_PARAMS, forgeImageData } from './forge';

/** Per-species palette for the MB.1 wood tiles: [plank, logSide, logEnd, leaves, sapling-leaf]. */
const WOOD_PALETTE: Record<WoodSpecies, { plank: [number, number, number]; bark: [number, number, number]; end: [number, number, number]; leaf: [number, number, number] }> = {
  spruce: { plank: [114, 84, 48], bark: [78, 54, 38], end: [104, 74, 48], leaf: [42, 92, 58] },
  birch: { plank: [196, 178, 123], bark: [214, 210, 194], end: [208, 200, 176], leaf: [108, 158, 68] },
  jungle: { plank: [160, 115, 80], bark: [86, 66, 40], end: [150, 120, 80], leaf: [42, 110, 32] },
  acacia: { plank: [168, 90, 50], bark: [104, 72, 52], end: [180, 120, 80], leaf: [96, 128, 40] },
  dark_oak: { plank: [66, 43, 20], bark: [58, 42, 28], end: [98, 74, 48], leaf: [58, 96, 30] },
  mangrove: { plank: [120, 54, 50], bark: [90, 54, 52], end: [130, 80, 70], leaf: [80, 124, 40] },
  cherry: { plank: [226, 178, 176], bark: [58, 40, 44], end: [210, 170, 168], leaf: [236, 158, 196] },
  pale_oak: { plank: [190, 180, 168], bark: [188, 184, 176], end: [200, 196, 188], leaf: [150, 168, 120] },
};

/** Base dye colours (M10.1), indexed by DYE_ORDER. */
const DYE_RGB: Record<string, [number, number, number]> = {
  white: [233, 236, 236], orange: [240, 118, 19], magenta: [199, 78, 189], light_blue: [58, 175, 217],
  yellow: [248, 198, 39], lime: [112, 185, 25], pink: [237, 141, 172], gray: [62, 68, 71],
  light_gray: [142, 142, 134], cyan: [21, 137, 145], purple: [121, 42, 172], blue: [53, 57, 157],
  brown: [114, 71, 40], green: [84, 109, 27], red: [160, 39, 34], black: [23, 24, 28],
};
const mix = (a: number, b: number, t: number): number => a + (b - a) * t;

const ORE_SPECK: Record<string, [number, number, number]> = {
  coal: [38, 38, 40], iron: [206, 170, 138], copper: [200, 118, 70], gold: [246, 224, 90],
  redstone: [220, 40, 40], lapis: [40, 82, 200], diamond: [122, 226, 226], emerald: [42, 206, 112],
};
const STONE_VAR: Record<string, [number, number, number]> = {
  andesite: [132, 132, 136], diorite: [206, 206, 212], granite: [168, 112, 92],
};

/** Small deterministic PRNG for texture noise (legacy mulberry32). */
function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function makeAtlasCanvas(): HTMLCanvasElement {
  const cvs = document.createElement('canvas');
  cvs.width = TILE_PX * ATLAS_TILES;
  cvs.height = TILE_PX;
  const ctx = cvs.getContext('2d')!;

  const px = (t: number, x: number, y: number, r: number, g: number, b: number, a = 255): void => {
    ctx.fillStyle = `rgba(${r | 0},${g | 0},${b | 0},${a / 255})`;
    ctx.fillRect(t * TILE_PX + x, y, 1, 1);
  };
  const noisyFill = (t: number, r: number, g: number, b: number, amt: number, rand: () => number): void => {
    for (let y = 0; y < TILE_PX; y++) {
      for (let x = 0; x < TILE_PX; x++) {
        const v = 1 + (rand() - 0.5) * amt;
        px(t, x, y, r * v, g * v, b * v);
      }
    }
  };

  let rand = mulberry32(101);
  noisyFill(TILE.GRASS_TOP, 96, 160, 66, 0.35, rand);

  rand = mulberry32(202);
  noisyFill(TILE.DIRT, 134, 96, 67, 0.35, rand);

  rand = mulberry32(303);
  noisyFill(TILE.GRASS_SIDE, 134, 96, 67, 0.35, rand);
  for (let x = 0; x < TILE_PX; x++) {
    const depth = 2 + Math.floor(rand() * 3);
    for (let y = 0; y < depth; y++) {
      const v = 1 + (rand() - 0.5) * 0.3;
      px(TILE.GRASS_SIDE, x, y, 96 * v, 160 * v, 66 * v);
    }
  }

  rand = mulberry32(404);
  noisyFill(TILE.STONE, 127, 127, 127, 0.22, rand);
  for (let i = 0; i < 14; i++) {
    const x = (rand() * TILE_PX) | 0, y = (rand() * TILE_PX) | 0;
    const len = (2 + rand() * 4) | 0, horiz = rand() < 0.5;
    for (let j = 0; j < len; j++) {
      px(TILE.STONE, (x + (horiz ? j : 0)) % TILE_PX, (y + (horiz ? 0 : j)) % TILE_PX, 96, 96, 96);
    }
  }

  rand = mulberry32(505);
  for (let x = 0; x < TILE_PX; x++) {
    const stripe = 0.8 + 0.35 * Math.abs(Math.sin(x * 1.7 + 0.9));
    for (let y = 0; y < TILE_PX; y++) {
      const v = stripe * (1 + (rand() - 0.5) * 0.18);
      px(TILE.WOOD_SIDE, x, y, 104 * v, 82 * v, 50 * v);
    }
  }

  rand = mulberry32(606);
  for (let y = 0; y < TILE_PX; y++) {
    for (let x = 0; x < TILE_PX; x++) {
      const d = Math.max(Math.abs(x - 7.5), Math.abs(y - 7.5));
      const ring = (d | 0) % 2 === 0 ? 1.0 : 0.78;
      const v = ring * (1 + (rand() - 0.5) * 0.12);
      px(TILE.WOOD_END, x, y, 168 * v, 136 * v, 88 * v);
    }
  }

  rand = mulberry32(707);
  ctx.clearRect(TILE.LEAVES * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let y = 0; y < TILE_PX; y++) {
    for (let x = 0; x < TILE_PX; x++) {
      if (rand() < 0.18) continue; // transparent hole
      const v = 0.7 + rand() * 0.6;
      px(TILE.LEAVES, x, y, 58 * v, 122 * v, 40 * v);
    }
  }

  // ── M2.1 blocks ──
  rand = mulberry32(808);
  noisyFill(TILE.SAND, 219, 205, 148, 0.14, rand);

  rand = mulberry32(909);
  noisyFill(TILE.SANDSTONE, 216, 202, 150, 0.1, rand);
  for (let x = 0; x < TILE_PX; x++) px(TILE.SANDSTONE, x, 3, 196, 182, 132); // faint band

  rand = mulberry32(1010);
  noisyFill(TILE.GRAVEL, 130, 126, 122, 0.4, rand);

  rand = mulberry32(1111);
  for (let y = 0; y < TILE_PX; y++) {
    for (let x = 0; x < TILE_PX; x++) {
      const v = 1 + Math.sin((x + y) * 0.9) * 0.06 + (rand() - 0.5) * 0.1;
      px(TILE.WATER, x, y, 54 * v, 108 * v, 200 * v, 200); // semi-transparent blue
    }
  }

  rand = mulberry32(1212);
  noisyFill(TILE.SNOW, 236, 242, 250, 0.06, rand);

  rand = mulberry32(1313);
  noisyFill(TILE.BEDROCK, 85, 85, 88, 0.4, rand);

  rand = mulberry32(1414);
  noisyFill(TILE.DEEPSLATE, 78, 79, 84, 0.22, rand);
  for (let i = 0; i < 12; i++) {
    const x = (rand() * TILE_PX) | 0, y = (rand() * TILE_PX) | 0;
    px(TILE.DEEPSLATE, x, y, 58, 59, 63);
  }

  // ── M2.2 blocks ──
  rand = mulberry32(1515);
  noisyFill(TILE.PLANK, 162, 130, 78, 0.12, rand);
  for (let y = 3; y < TILE_PX; y += 5) for (let x = 0; x < TILE_PX; x++) px(TILE.PLANK, x, y, 126, 98, 58);

  // Tall grass (cutout blades on transparent background).
  rand = mulberry32(1616);
  ctx.clearRect(TILE.GRASS_TALL * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let x = 0; x < TILE_PX; x++) {
    if (rand() < 0.4) continue;
    const top = 4 + Math.floor(rand() * 6);
    for (let y = top; y < TILE_PX; y++) {
      const v = 0.6 + rand() * 0.5;
      px(TILE.GRASS_TALL, x, y, 70 * v, 140 * v, 50 * v);
    }
  }

  // Dandelion (stem + yellow head).
  ctx.clearRect(TILE.FLOWER * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let y = 7; y < TILE_PX; y++) px(TILE.FLOWER, 8, y, 60, 130, 50);
  for (let y = 3; y < 8; y++) for (let x = 6; x < 11; x++) {
    if (Math.abs(x - 8) + Math.abs(y - 5) < 4) px(TILE.FLOWER, x, y, 232, 212, 62);
  }

  // Torch (stick + flame).
  ctx.clearRect(TILE.TORCH * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let y = 6; y < TILE_PX; y++) for (let x = 7; x < 9; x++) px(TILE.TORCH, x, y, 122, 82, 42);
  for (let y = 3; y < 6; y++) for (let x = 6; x < 10; x++) px(TILE.TORCH, x, y, 250, 220, 92);

  // ── M2.3 ores + stone variants (base + colored specks) ──
  for (const [name, tile] of Object.entries(EXTRA_TILES)) {
    rand = mulberry32(tile * 131 + 7);
    const variant = STONE_VAR[name];
    if (variant) {
      noisyFill(tile, variant[0], variant[1], variant[2], 0.18, rand);
      continue;
    }
    const deep = name.startsWith('deepslate_');
    const ore = name.replace('deepslate_', '').replace('_ore', '');
    const base: [number, number, number] = deep ? [80, 81, 86] : [127, 127, 127];
    noisyFill(tile, base[0], base[1], base[2], 0.2, rand);
    const [sr, sg, sb] = ORE_SPECK[ore]!;
    for (let i = 0; i < 8; i++) {
      const bx = (rand() * 14) | 0, by = (rand() * 14) | 0;
      const v = 0.8 + rand() * 0.4;
      for (let dx = 0; dx < 2; dx++) for (let dy = 0; dy < 2; dy++) px(tile, bx + dx, by + dy, sr * v, sg * v, sb * v);
    }
  }

  // ── M2.4 trees + vegetation ──
  // Birch: pale bark with dark lenticel dashes; light-green canopy.
  rand = mulberry32(1717);
  noisyFill(VEG.BIRCH_SIDE, 214, 210, 194, 0.08, rand);
  for (let i = 0; i < 5; i++) {
    const y = (rand() * TILE_PX) | 0, x = (rand() * 11) | 0, len = 2 + ((rand() * 3) | 0);
    for (let j = 0; j < len; j++) px(VEG.BIRCH_SIDE, x + j, y, 60, 58, 52);
  }
  rand = mulberry32(1818);
  for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) {
    const d = Math.max(Math.abs(x - 7.5), Math.abs(y - 7.5));
    const v = ((d | 0) % 2 === 0 ? 1.0 : 0.86) * (1 + (rand() - 0.5) * 0.1);
    px(VEG.BIRCH_END, x, y, 208 * v, 200 * v, 176 * v);
  }
  rand = mulberry32(1919);
  ctx.clearRect(VEG.BIRCH_LEAVES * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) {
    if (rand() < 0.16) continue;
    const v = 0.75 + rand() * 0.5;
    px(VEG.BIRCH_LEAVES, x, y, 108 * v, 158 * v, 68 * v);
  }

  // Spruce: dark reddish bark; deep blue-green canopy.
  rand = mulberry32(2020);
  for (let x = 0; x < TILE_PX; x++) {
    const stripe = 0.8 + 0.3 * Math.abs(Math.sin(x * 1.9 + 0.4));
    for (let y = 0; y < TILE_PX; y++) {
      const v = stripe * (1 + (rand() - 0.5) * 0.22);
      px(VEG.SPRUCE_SIDE, x, y, 78 * v, 54 * v, 38 * v);
    }
  }
  rand = mulberry32(2121);
  for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) {
    const d = Math.max(Math.abs(x - 7.5), Math.abs(y - 7.5));
    const v = ((d | 0) % 2 === 0 ? 1.0 : 0.8) * (1 + (rand() - 0.5) * 0.14);
    px(VEG.SPRUCE_END, x, y, 104 * v, 74 * v, 48 * v);
  }
  rand = mulberry32(2222);
  ctx.clearRect(VEG.SPRUCE_LEAVES * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) {
    if (rand() < 0.2) continue;
    const v = 0.7 + rand() * 0.55;
    px(VEG.SPRUCE_LEAVES, x, y, 42 * v, 92 * v, 58 * v);
  }

  // Cactus: green body with vertical ribs; lighter top with a cross of areoles.
  rand = mulberry32(2323);
  noisyFill(VEG.CACTUS_SIDE, 74, 128, 58, 0.14, rand);
  for (const rx of [3, 8, 12]) for (let y = 0; y < TILE_PX; y++) px(VEG.CACTUS_SIDE, rx, y, 52, 96, 42);
  rand = mulberry32(2424);
  noisyFill(VEG.CACTUS_TOP, 92, 150, 72, 0.12, rand);
  for (let i = 0; i < TILE_PX; i++) { px(VEG.CACTUS_TOP, 7, i, 60, 110, 50); px(VEG.CACTUS_TOP, i, 8, 60, 110, 50); }

  // Dead bush: bare brown twigs on transparent.
  rand = mulberry32(2525);
  ctx.clearRect(VEG.DEAD_BUSH * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let x = 4; x < 12; x++) {
    if (rand() < 0.5) continue;
    const top = 5 + ((rand() * 5) | 0);
    for (let y = top; y < TILE_PX; y++) px(VEG.DEAD_BUSH, x, y, 122, 92, 52);
  }
  for (let y = 8; y < TILE_PX; y++) px(VEG.DEAD_BUSH, 8, y, 108, 80, 44);

  // Sugar cane: a slim green vertical stalk on transparent.
  rand = mulberry32(2626);
  ctx.clearRect(VEG.SUGAR_CANE * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let y = 0; y < TILE_PX; y++) for (let x = 7; x < 10; x++) {
    const v = 0.8 + rand() * 0.35;
    px(VEG.SUGAR_CANE, x, y, 118 * v, 176 * v, 96 * v);
  }

  // Poppy: green stem with a red bloom.
  ctx.clearRect(VEG.POPPY * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let y = 7; y < TILE_PX; y++) px(VEG.POPPY, 8, y, 60, 130, 50);
  for (let y = 2; y < 8; y++) for (let x = 5; x < 12; x++) {
    if (Math.abs(x - 8) + Math.abs(y - 4) < 4) px(VEG.POPPY, x, y, 208, 44, 44);
  }
  px(VEG.POPPY, 8, 4, 40, 30, 20); // dark center

  // ── M4.2 crafted blocks ──
  // Cobblestone: clustered mid-grey cobbles with dark mortar gaps.
  rand = mulberry32(2727);
  noisyFill(M4T.COBBLESTONE, 120, 120, 122, 0.28, rand);
  for (let i = 0; i < 10; i++) {
    const cx = 1 + ((rand() * 13) | 0), cy = 1 + ((rand() * 13) | 0), r = 1 + ((rand() * 2) | 0);
    for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) {
      if (dx * dx + dy * dy > r * r) continue;
      const v = 0.85 + rand() * 0.4;
      px(M4T.COBBLESTONE, cx + dx, cy + dy, 138 * v, 138 * v, 140 * v);
    }
  }
  for (let i = 0; i < 22; i++) px(M4T.COBBLESTONE, (rand() * TILE_PX) | 0, (rand() * TILE_PX) | 0, 70, 70, 72);

  // Crafting table: plank base with a darker grid; top shows a saw + grid.
  rand = mulberry32(2828);
  noisyFill(M4T.CRAFTING_SIDE, 150, 118, 70, 0.12, rand);
  for (let x = 0; x < TILE_PX; x++) { px(M4T.CRAFTING_SIDE, x, 0, 90, 68, 40); px(M4T.CRAFTING_SIDE, x, 15, 90, 68, 40); }
  for (let y = 0; y < TILE_PX; y++) { px(M4T.CRAFTING_SIDE, 0, y, 90, 68, 40); px(M4T.CRAFTING_SIDE, 15, y, 90, 68, 40); }
  rand = mulberry32(2829);
  noisyFill(M4T.CRAFTING_TOP, 162, 130, 78, 0.1, rand);
  for (let x = 0; x < TILE_PX; x++) px(M4T.CRAFTING_TOP, x, 8, 108, 82, 48);
  for (let y = 0; y < TILE_PX; y++) px(M4T.CRAFTING_TOP, 8, y, 108, 82, 48);

  // Furnace: stone body; top shows a hopper hole; front a dark firebox arch.
  rand = mulberry32(2830);
  noisyFill(M4T.FURNACE_SIDE, 116, 116, 118, 0.2, rand);
  rand = mulberry32(2831);
  noisyFill(M4T.FURNACE_TOP, 116, 116, 118, 0.2, rand);
  for (let y = 6; y < 10; y++) for (let x = 6; x < 10; x++) px(M4T.FURNACE_TOP, x, y, 60, 60, 62);
  rand = mulberry32(2832);
  noisyFill(M4T.FURNACE_FRONT, 116, 116, 118, 0.2, rand);
  for (let y = 5; y < 13; y++) for (let x = 4; x < 12; x++) px(M4T.FURNACE_FRONT, x, y, 46, 44, 44); // firebox
  for (let x = 5; x < 11; x++) px(M4T.FURNACE_FRONT, x, 4, 92, 92, 94); // lintel

  // Chest: wood body with a dark band + a metal latch.
  rand = mulberry32(2833);
  noisyFill(M4T.CHEST, 150, 112, 60, 0.12, rand);
  for (let x = 0; x < TILE_PX; x++) { px(M4T.CHEST, x, 5, 96, 68, 34); px(M4T.CHEST, x, 6, 120, 88, 44); }
  for (let y = 6; y < 9; y++) for (let x = 7; x < 9; x++) px(M4T.CHEST, x, y, 210, 200, 120); // latch
  for (let y = 0; y < TILE_PX; y++) { px(M4T.CHEST, 0, y, 96, 68, 34); px(M4T.CHEST, 15, y, 96, 68, 34); }

  // ── M7 nether blocks ──
  // Netherrack: dark red, mottled.
  rand = mulberry32(3101);
  noisyFill(NETHER.NETHERRACK, 108, 46, 46, 0.36, rand);
  for (let i = 0; i < 10; i++) px(NETHER.NETHERRACK, (rand() * TILE_PX) | 0, (rand() * TILE_PX) | 0, 74, 30, 30);

  // Glowstone: warm yellow lumps with bright cores.
  rand = mulberry32(3202);
  noisyFill(NETHER.GLOWSTONE, 190, 150, 74, 0.18, rand);
  for (let i = 0; i < 12; i++) {
    const cx = 1 + ((rand() * 13) | 0), cy = 1 + ((rand() * 13) | 0);
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
      const v = 0.9 + rand() * 0.4;
      px(NETHER.GLOWSTONE, cx + dx, cy + dy, 246 * v, 214 * v, 120 * v);
    }
  }

  // Soul sand: brown with dark sunken "faces".
  rand = mulberry32(3303);
  noisyFill(NETHER.SOUL_SAND, 84, 64, 52, 0.2, rand);
  for (const [fx, fy] of [[4, 6], [10, 9]] as const) for (let dy = 0; dy < 4; dy++) for (let dx = 0; dx < 4; dx++) px(NETHER.SOUL_SAND, fx + dx, fy + dy, 52, 40, 34);

  // Magma block: cracked dark rock with glowing lava veins.
  rand = mulberry32(3404);
  noisyFill(NETHER.MAGMA, 44, 30, 26, 0.3, rand);
  for (let i = 0; i < 22; i++) px(NETHER.MAGMA, (rand() * TILE_PX) | 0, (rand() * TILE_PX) | 0, 236, 128, 40);

  // Obsidian: near-black with faint violet sheen.
  rand = mulberry32(3505);
  noisyFill(NETHER.OBSIDIAN, 22, 18, 34, 0.22, rand);
  for (let i = 0; i < 8; i++) px(NETHER.OBSIDIAN, (rand() * TILE_PX) | 0, (rand() * TILE_PX) | 0, 66, 44, 96);

  // Nether portal: swirling purple (translucent plane).
  rand = mulberry32(3606);
  for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) {
    const v = 0.7 + Math.abs(Math.sin((x + y) * 0.8)) * 0.5 + (rand() - 0.5) * 0.15;
    px(NETHER.PORTAL, x, y, 132 * v, 52 * v, 196 * v, 210);
  }

  // Nether quartz ore: netherrack with pale quartz specks.
  rand = mulberry32(3707);
  noisyFill(NETHER.QUARTZ_ORE, 108, 46, 46, 0.3, rand);
  for (let i = 0; i < 8; i++) {
    const bx = (rand() * 14) | 0, by = (rand() * 14) | 0;
    for (let dx = 0; dx < 2; dx++) for (let dy = 0; dy < 2; dy++) px(NETHER.QUARTZ_ORE, bx + dx, by + dy, 232, 222, 214);
  }

  // Lava: bright orange with darker crust flecks (translucent pass).
  rand = mulberry32(3808);
  for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) {
    const v = 1 + Math.sin((x * 0.7 + y * 1.3)) * 0.12 + (rand() - 0.5) * 0.12;
    px(NETHER.LAVA, x, y, 226 * v, 108 * v, 30 * v, 235);
  }
  for (let i = 0; i < 10; i++) px(NETHER.LAVA, (rand() * TILE_PX) | 0, (rand() * TILE_PX) | 0, 120, 44, 20);

  // ── M8 Atlas blocks ──
  // Glass: translucent pale blue with a light frame (renders in the water pass).
  ctx.clearRect(ATLAS_BLK.GLASS * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) px(ATLAS_BLK.GLASS, x, y, 200, 224, 236, 90);
  for (let i = 0; i < TILE_PX; i++) { px(ATLAS_BLK.GLASS, i, 0, 224, 240, 250, 200); px(ATLAS_BLK.GLASS, i, 15, 224, 240, 250, 200); px(ATLAS_BLK.GLASS, 0, i, 224, 240, 250, 200); px(ATLAS_BLK.GLASS, 15, i, 224, 240, 250, 200); }

  // Bricks: red-brown running-bond courses with mortar lines.
  rand = mulberry32(4101);
  noisyFill(ATLAS_BLK.BRICKS, 154, 84, 66, 0.14, rand);
  for (let y = 3; y < TILE_PX; y += 4) for (let x = 0; x < TILE_PX; x++) px(ATLAS_BLK.BRICKS, x, y, 96, 74, 66); // mortar rows
  for (let y = 0; y < TILE_PX; y++) { const off = (Math.floor(y / 4) % 2) * 8; for (let x = (off + 3) % 8; x < TILE_PX; x += 8) px(ATLAS_BLK.BRICKS, x, y, 96, 74, 66); }

  // Stone bricks: grey blocks with dark mortar grid.
  rand = mulberry32(4202);
  noisyFill(ATLAS_BLK.STONE_BRICKS, 130, 130, 134, 0.12, rand);
  for (let y = 0; y < TILE_PX; y += 8) for (let x = 0; x < TILE_PX; x++) px(ATLAS_BLK.STONE_BRICKS, x, y, 92, 92, 96);
  for (let x = 0; x < TILE_PX; x += 8) for (let y = 0; y < TILE_PX; y++) px(ATLAS_BLK.STONE_BRICKS, (x + (Math.floor(y / 8) % 2) * 4) % TILE_PX, y, 92, 92, 96);

  // MB.1 clay: soft blue-grey, smooth with faint speckle.
  rand = mulberry32(4310);
  noisyFill(MB10T.CLAY, 162, 166, 176, 0.08, rand);
  for (let i = 0; i < 14; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(MB10T.CLAY, x, y, 150, 154, 166); }
  // MB.1 base terracotta: hardened-clay orange-brown with faint horizontal banding.
  rand = mulberry32(4311);
  noisyFill(MB10T.TERRACOTTA, 152, 94, 68, 0.12, rand);
  for (let y = 0; y < TILE_PX; y += 4) for (let x = 0; x < TILE_PX; x++) px(MB10T.TERRACOTTA, x, y, 152 * 0.85, 94 * 0.85, 68 * 0.85);

  // E-14 slime block: translucent green with a darker inner cube outline (the
  // classic slime look). The mesher draws it in the translucent pass.
  rand = mulberry32(4320);
  noisyFill(MB11T.SLIME, 108, 190, 104, 0.1, rand);
  for (let i = 2; i < TILE_PX - 2; i++) { px(MB11T.SLIME, i, 2, 72, 150, 70); px(MB11T.SLIME, i, TILE_PX - 3, 72, 150, 70); px(MB11T.SLIME, 2, i, 72, 150, 70); px(MB11T.SLIME, TILE_PX - 3, i, 72, 150, 70); }
  // E-14 honey block: translucent amber with faint diagonal drip streaks.
  rand = mulberry32(4321);
  noisyFill(MB11T.HONEY, 226, 158, 46, 0.1, rand);
  for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) if ((x + y) % 5 === 0) px(MB11T.HONEY, x, y, 210, 138, 32);

  // Gray concrete: flat mid-grey road surface, faint aggregate.
  rand = mulberry32(4303);
  noisyFill(ATLAS_BLK.GRAY_CONCRETE, 124, 127, 130, 0.1, rand);
  for (let i = 0; i < 10; i++) px(ATLAS_BLK.GRAY_CONCRETE, (rand() * TILE_PX) | 0, (rand() * TILE_PX) | 0, 104, 107, 110);

  // Ladder: two brown rails + rungs on transparent (cross-shaped, non-solid).
  ctx.clearRect(ATLAS_BLK.LADDER * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let y = 0; y < TILE_PX; y++) { px(ATLAS_BLK.LADDER, 4, y, 130, 96, 52); px(ATLAS_BLK.LADDER, 11, y, 130, 96, 52); }
  for (let y = 2; y < TILE_PX; y += 4) for (let x = 4; x <= 11; x++) px(ATLAS_BLK.LADDER, x, y, 150, 112, 62);

  // ── M9 blocks (weather/beds) ──
  // White wool: soft near-white with faint fibre noise.
  rand = mulberry32(5101);
  noisyFill(M9T.WHITE_WOOL, 232, 232, 228, 0.08, rand);
  for (let i = 0; i < 30; i++) px(M9T.WHITE_WOOL, (rand() * TILE_PX) | 0, (rand() * TILE_PX) | 0, 212, 212, 208);

  // Bed: red mattress top with a pale pillow band; wooden-trim sides.
  rand = mulberry32(5202);
  noisyFill(M9T.BED_TOP, 196, 54, 54, 0.1, rand);
  for (let y = 0; y < 4; y++) for (let x = 0; x < TILE_PX; x++) px(M9T.BED_TOP, x, y, 236, 236, 232); // pillow
  rand = mulberry32(5203);
  noisyFill(M9T.BED_SIDE, 176, 48, 48, 0.1, rand);
  for (let x = 0; x < TILE_PX; x++) px(M9T.BED_SIDE, x, 0, 150, 112, 62);   // wood rail top
  for (let x = 0; x < TILE_PX; x++) px(M9T.BED_SIDE, x, 15, 120, 88, 48);  // wood rail bottom

  // ── M10 coloured families (data-driven tint per family × dye colour) ──
  for (const color of DYE_ORDER) {
    const [br, bg, bb] = DYE_RGB[color]!;
    for (const family of FAMILY_ORDER) {
      const tile = familyTileOf(`${color}_${family}`)!;
      rand = mulberry32(tile * 97 + 13);
      if (family === 'stained_glass') {
        for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) px(tile, x, y, br, bg, bb, 110);
        for (let i = 0; i < TILE_PX; i++) { px(tile, i, 0, 235, 240, 245, 190); px(tile, i, 15, 235, 240, 245, 190); px(tile, 0, i, 235, 240, 245, 190); px(tile, 15, i, 235, 240, 245, 190); }
        continue;
      }
      // Opaque families: mix the base toward a family tone, then add grain.
      let r = br, g = bg, b = bb, amt = 0.14;
      if (family === 'wool') { r = mix(br, 235, 0.18); g = mix(bg, 235, 0.18); b = mix(bb, 235, 0.18); amt = 0.1; }
      else if (family === 'concrete') { amt = 0.05; }
      else if (family === 'concrete_powder') { r = mix(br, 240, 0.28); g = mix(bg, 235, 0.28); b = mix(bb, 225, 0.28); amt = 0.22; }
      else if (family === 'terracotta') { r = mix(br, 150, 0.42); g = mix(bg, 96, 0.42); b = mix(bb, 74, 0.42); amt = 0.16; }
      noisyFill(tile, r, g, b, amt, rand);
      if (family === 'terracotta') for (let y = 0; y < TILE_PX; y += 4) for (let x = 0; x < TILE_PX; x++) px(tile, x, y, r * 0.85, g * 0.85, b * 0.85);
    }
  }

  // ── MB.1 glazed terracotta ×16 (a directional diagonal glaze so the per-facing
  //    top rotation reads; one tile per dye colour). ──
  for (const color of DYE_ORDER) {
    const tile = glazedTerracottaTileOf(`${color}_glazed_terracotta`);
    if (tile === undefined) continue;
    const [br, bg, bb] = DYE_RGB[color]!;
    // A glazed terracotta base: the terracotta tone, a touch brighter + smoother.
    const r = mix(br, 170, 0.32), g = mix(bg, 130, 0.32), b = mix(bb, 110, 0.32);
    rand = mulberry32(tile * 131 + 7);
    noisyFill(tile, r, g, b, 0.06, rand);
    // A light glaze wedge in one corner (asymmetric → rotation is visible), plus a
    // darker border stripe on two edges for a ceramic look.
    for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) {
      if (x + y < TILE_PX / 2) px(tile, x, y, mix(r, 255, 0.55), mix(g, 255, 0.55), mix(b, 255, 0.55));
    }
    for (let i = 0; i < TILE_PX; i++) { px(tile, i, 0, r * 0.7, g * 0.7, b * 0.7); px(tile, 0, i, r * 0.7, g * 0.7, b * 0.7); }
  }

  // ── M11 farming tiles ──
  // Farmland: tilled brown soil with darker furrows; wet variant is darker.
  rand = mulberry32(6001);
  noisyFill(M11T.FARMLAND_DRY, 122, 88, 58, 0.12, rand);
  for (let y = 3; y < TILE_PX; y += 6) for (let x = 0; x < TILE_PX; x++) px(M11T.FARMLAND_DRY, x, y, 96, 68, 42);
  rand = mulberry32(6002);
  noisyFill(M11T.FARMLAND_WET, 86, 58, 36, 0.12, rand);
  for (let y = 3; y < TILE_PX; y += 6) for (let x = 0; x < TILE_PX; x++) px(M11T.FARMLAND_WET, x, y, 62, 42, 26);

  // Crops (young sparse green → mature): cutout blades on transparent.
  const cropTile = (tile: number, seed: number, tops: [number, number, number], density: number): void => {
    rand = mulberry32(seed);
    ctx.clearRect(tile * TILE_PX, 0, TILE_PX, TILE_PX);
    for (let x = 1; x < TILE_PX; x += 3) {
      if (rand() > density) continue;
      const top = 3 + Math.floor(rand() * 6);
      for (let y = top; y < TILE_PX; y++) px(tile, x, y, 70 + rand() * 30, 120 + rand() * 40, 50 + rand() * 20);
      for (let y = top; y < top + 3; y++) px(tile, x, y, tops[0], tops[1], tops[2]); // ripe tip
    }
  };
  cropTile(M11T.WHEAT_YOUNG, 6100, [90, 150, 60], 0.5);
  cropTile(M11T.WHEAT_MATURE, 6101, [214, 190, 90], 1); // golden heads
  cropTile(M11T.CARROTS_YOUNG, 6102, [90, 150, 60], 0.5);
  cropTile(M11T.CARROTS_MATURE, 6103, [232, 140, 40], 1);
  cropTile(M11T.POTATOES_YOUNG, 6104, [90, 150, 60], 0.5);
  cropTile(M11T.POTATOES_MATURE, 6105, [200, 190, 120], 1);
  cropTile(M11T.BEETROOTS_YOUNG, 6106, [90, 150, 60], 0.5);
  cropTile(M11T.BEETROOTS_MATURE, 6107, [180, 40, 60], 1);
  cropTile(M61T.COTTON_YOUNG, 6108, [90, 150, 60], 0.5);
  cropTile(M61T.COTTON_MATURE, 6109, [242, 240, 232], 1); // white bolls

  // Hydroponic tray: a steel basin of nutrient water — grows crops at full
  // moisture with no irrigation, so no water has to be hauled to the field.
  rand = mulberry32(6110);
  noisyFill(M61T.HYDRO_SIDE, 150, 156, 166, 0.10, rand);
  for (let x = 0; x < TILE_PX; x++) { px(M61T.HYDRO_SIDE, x, 0, 108, 114, 124); px(M61T.HYDRO_SIDE, x, TILE_PX - 1, 108, 114, 124); }
  noisyFill(M61T.HYDRO_TOP, 60, 120, 150, 0.12, rand); // nutrient water
  for (let i = 0; i < 4; i++) { // steel rim
    for (let x = 0; x < TILE_PX; x++) { px(M61T.HYDRO_TOP, x, i < 2 ? i : TILE_PX - 1 - (i - 2), 150, 156, 166); }
    for (let y = 0; y < TILE_PX; y++) { px(M61T.HYDRO_TOP, i < 2 ? i : TILE_PX - 1 - (i - 2), y, 150, 156, 166); }
  }

  // Composter: wooden slatted box with a dark open top.
  rand = mulberry32(6200);
  noisyFill(M11T.COMPOSTER, 150, 116, 66, 0.12, rand);
  for (let x = 3; x < TILE_PX; x += 4) for (let y = 0; y < TILE_PX; y++) px(M11T.COMPOSTER, x, y, 108, 82, 46);
  for (let y = 0; y < 3; y++) for (let x = 0; x < TILE_PX; x++) px(M11T.COMPOSTER, x, y, 60, 44, 26);

  // Enchanting table top: dark obsidian with a violet glyph glow.
  rand = mulberry32(6300);
  noisyFill(M11T.ENCHANT_TOP, 30, 24, 44, 0.2, rand);
  for (let i = 0; i < 10; i++) px(M11T.ENCHANT_TOP, 4 + ((rand() * 8) | 0), 4 + ((rand() * 8) | 0), 150, 90, 220);

  // Bookshelf: plank frame with coloured book spines.
  rand = mulberry32(6301);
  noisyFill(M11T.BOOKSHELF, 150, 116, 66, 0.12, rand);
  for (let y = 1; y < 15; y++) { px(M11T.BOOKSHELF, 0, y, 108, 82, 46); px(M11T.BOOKSHELF, 15, y, 108, 82, 46); }
  for (let x = 1; x < 15; x++) {
    if (rand() < 0.3) continue;
    const c = [[180, 60, 60], [60, 120, 180], [80, 160, 80], [200, 180, 70]][(rand() * 4) | 0]!;
    for (let y = 2; y < 7; y++) px(M11T.BOOKSHELF, x, y, c[0]!, c[1]!, c[2]!);
    for (let y = 9; y < 14; y++) px(M11T.BOOKSHELF, x, y, c[0]! * 0.8, c[1]! * 0.8, c[2]! * 0.8);
  }

  // M13 golem-construction blocks.
  // Iron block: light metal with a bevelled tile border.
  rand = mulberry32(6400);
  noisyFill(M13T.IRON_BLOCK, 216, 216, 216, 0.06, rand);
  for (let i = 0; i < TILE_PX; i++) { px(M13T.IRON_BLOCK, i, 0, 236, 236, 236); px(M13T.IRON_BLOCK, 0, i, 236, 236, 236); px(M13T.IRON_BLOCK, i, 15, 190, 190, 190); px(M13T.IRON_BLOCK, 15, i, 190, 190, 190); }
  // Pumpkin: orange skin with vertical ridges (side) + stem knob (top).
  rand = mulberry32(6401);
  noisyFill(M13T.PUMPKIN_SIDE, 200, 118, 26, 0.12, rand);
  for (let x = 2; x < TILE_PX; x += 4) for (let y = 0; y < TILE_PX; y++) px(M13T.PUMPKIN_SIDE, x, y, 168, 94, 20);
  rand = mulberry32(6402);
  noisyFill(M13T.PUMPKIN_TOP, 190, 112, 24, 0.12, rand);
  for (let y = 6; y < 10; y++) for (let x = 6; x < 10; x++) px(M13T.PUMPKIN_TOP, x, y, 120, 96, 50); // stem
  // Carved pumpkin face: a dark jack-o'-lantern grin over the side skin.
  rand = mulberry32(6403);
  noisyFill(M13T.CARVED_PUMPKIN_FRONT, 200, 118, 26, 0.12, rand);
  for (let x = 2; x < TILE_PX; x += 4) for (let y = 0; y < TILE_PX; y++) px(M13T.CARVED_PUMPKIN_FRONT, x, y, 168, 94, 20);
  const dark = (x: number, y: number): void => px(M13T.CARVED_PUMPKIN_FRONT, x, y, 40, 26, 8);
  for (const [ex, ey] of [[4, 5], [5, 5], [5, 6], [10, 5], [11, 5], [10, 6]] as const) dark(ex, ey); // eyes
  for (let x = 4; x <= 11; x++) dark(x, 10); // mouth line
  for (const [mx, my] of [[5, 11], [7, 11], [9, 11], [6, 9], [10, 9]] as const) dark(mx, my); // grin teeth

  // ── M19 amethyst geode blocks ──
  // Amethyst block: faceted violet crystal with lighter/darker facets.
  rand = mulberry32(1901);
  noisyFill(M19T.AMETHYST_BLOCK, 148, 96, 214, 0.14, rand);
  for (const [fx, fy, w, h, m] of [[1, 1, 6, 6, 1.2], [9, 2, 5, 5, 0.82], [3, 9, 6, 5, 0.9], [10, 9, 4, 4, 1.15]] as const)
    for (let y = fy; y < fy + h; y++) for (let x = fx; x < fx + w; x++) px(M19T.AMETHYST_BLOCK, x, y, 148 * m, 96 * m, 214 * m);
  // Budding amethyst: darker matrix speckled with tiny sprouting crystals.
  rand = mulberry32(1902);
  noisyFill(M19T.BUDDING_AMETHYST, 120, 74, 176, 0.16, rand);
  for (let i = 0; i < 22; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M19T.BUDDING_AMETHYST, x, y, 206, 168, 244); }
  // Calcite: near-white with faint grey grain.
  rand = mulberry32(1903);
  noisyFill(M19T.CALCITE, 223, 224, 220, 0.07, rand);
  // Smooth basalt: dark grey, subtle grain.
  rand = mulberry32(1904);
  noisyFill(M19T.SMOOTH_BASALT, 72, 71, 77, 0.10, rand);
  // Amethyst crystal cluster: a cutout billboard (transparent background).
  ctx.clearRect(M19T.AMETHYST_CRYSTAL * TILE_PX, 0, TILE_PX, TILE_PX);
  const shard = (cx: number, top: number, bottom: number, tint: number): void => {
    for (let y = top; y <= bottom; y++) {
      const half = Math.max(0, Math.round((y - top) * 1.4)) - Math.max(0, Math.round((y - bottom + 2)));
      for (let x = cx - half; x <= cx + half; x++) {
        const edge = x === cx - half || x === cx + half;
        px(M19T.AMETHYST_CRYSTAL, x, y, 150 * tint * (edge ? 0.82 : 1), 96 * tint * (edge ? 0.82 : 1), 216 * tint * (edge ? 0.82 : 1));
      }
    }
  };
  shard(8, 4, 14, 1.1); // tall centre spire
  shard(4, 8, 14, 0.9); // left
  shard(12, 9, 14, 0.95); // right

  // ── Sculk family (Deep Dark, M22). Very dark blue-teal speckled with cyan
  //    glow-flecks; the catalyst's top glows brightest where it bloomed. ──
  rand = mulberry32(2201);
  noisyFill(M22T.SCULK, 13, 26, 31, 0.18, rand);
  for (let i = 0; i < 10; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M22T.SCULK, x, y, 30, 120, 130); }
  rand = mulberry32(2202);
  noisyFill(M22T.SCULK_CATALYST_SIDE, 16, 30, 36, 0.16, rand);
  for (let y = 0; y < TILE_PX; y++) { const x = (2 + (rand() * 12)) | 0; px(M22T.SCULK_CATALYST_SIDE, x, y, 40, 150, 160); } // trickling veins
  rand = mulberry32(2203);
  noisyFill(M22T.SCULK_CATALYST_TOP, 20, 40, 46, 0.14, rand);
  for (let i = 0; i < 26; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M22T.SCULK_CATALYST_TOP, x, y, 74, 224, 224); } // bloom glow

  // ── Beacon (M20). A pale quartz-glass field with a bright cyan-white star core
  //    radiating toward the corners. ──
  rand = mulberry32(2010);
  noisyFill(M20T.BEACON, 150, 210, 214, 0.10, rand);
  for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) {
    const dx = x - 7.5, dy = y - 7.5;
    const d = Math.hypot(dx, dy);
    if (d < 2.4) px(M20T.BEACON, x, y, 236, 255, 255);            // white-hot core
    else if (d < 4.2) px(M20T.BEACON, x, y, 150, 244, 250);       // cyan halo
    else if (Math.abs(Math.abs(dx) - Math.abs(dy)) < 1.1 && d < 8) px(M20T.BEACON, x, y, 120, 224, 232); // diagonal rays
  }
  // Wither skeleton skull — charred grey bone with two dark eye sockets.
  rand = mulberry32(2011);
  noisyFill(M20T.WITHER_SKULL, 60, 60, 62, 0.12, rand);
  for (let y = 3; y <= 12; y++) for (let x = 4; x <= 11; x++) px(M20T.WITHER_SKULL, x, y, 74, 74, 76); // cranium
  for (const [ex, ey] of [[6, 7], [9, 7]] as const) for (let dx = 0; dx <= 1; dx++) for (let dy = 0; dy <= 1; dy++) px(M20T.WITHER_SKULL, ex + dx, ey + dy, 18, 18, 20); // eye sockets
  for (let x = 6; x <= 9; x++) px(M20T.WITHER_SKULL, x, 11, 34, 34, 36); // jaw line

  // ── M18 ocean blocks. Prismarine's mottled teal (bricks/dark variants shift the
  //    base + seam colour); sea lantern glows; the conduit is a shell-cage eye. ──
  const prismarine = (tile: number, br: number, bg: number, bb: number, seed: number): void => {
    rand = mulberry32(seed);
    noisyFill(tile, br, bg, bb, 0.14, rand);
    for (let g = 4; g < TILE_PX; g += 4) for (let x = 0; x < TILE_PX; x++) { px(tile, x, g, br * 0.7, bg * 0.7, bb * 0.7); px(tile, g, x, br * 0.7, bg * 0.7, bb * 0.7); } // brick seams
  };
  prismarine(M18T.PRISMARINE, 90, 156, 143, 1801);
  prismarine(M18T.PRISMARINE_BRICKS, 90, 168, 152, 1802);
  prismarine(M18T.DARK_PRISMARINE, 49, 86, 76, 1803);
  rand = mulberry32(1804);
  noisyFill(M18T.SEA_LANTERN, 185, 214, 207, 0.08, rand);
  for (const [cx, cy] of [[4, 4], [11, 4], [4, 11], [11, 11], [8, 8]] as const) for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) px(M18T.SEA_LANTERN, cx + dx, cy + dy, 236, 248, 244); // glowing pearls
  rand = mulberry32(1805);
  noisyFill(M18T.CONDUIT, 120, 104, 60, 0.12, rand);
  for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) {
    const d = Math.hypot(x - 7.5, y - 7.5);
    if (d < 2.2) px(M18T.CONDUIT, x, y, 236, 240, 210);       // the open eye
    else if (d < 3.6) px(M18T.CONDUIT, x, y, 70, 150, 140);   // teal iris
    else if (d > 6.2) px(M18T.CONDUIT, x, y, 210, 196, 150);  // bone shell
  }
  // Sculk shrieker — dark sculk with a bright cyan ridged shriek maw (M22).
  rand = mulberry32(2210);
  noisyFill(M22B.SCULK_SHRIEKER, 16, 32, 38, 0.16, rand);
  for (let r = 2; r <= 6; r += 2) for (let a = 0; a < 32; a++) {
    const x = Math.round(7.5 + r * Math.cos((a / 32) * Math.PI * 2));
    const y = Math.round(7.5 + r * Math.sin((a / 32) * Math.PI * 2));
    px(M22B.SCULK_SHRIEKER, x, y, 46, 190, 200); // concentric shriek ridges
  }
  for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) px(M22B.SCULK_SHRIEKER, 8 + dx, 8 + dy, 150, 244, 250); // glowing centre

  // Sculk sensor — dark sculk with two glowing cyan tendrils splayed from the core
  //  (M22); the calibrated one adds an amethyst-lined frame.
  rand = mulberry32(4001);
  for (const [tile, framed] of [[M40T.SCULK_SENSOR, false], [M40T.CALIBRATED_SCULK_SENSOR, true]] as const) {
    noisyFill(tile, 14, 30, 36, 0.16, rand);
    for (let i = 0; i < 5; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(tile, x, y, 30, 120, 130); }
    for (let t = 0; t < 8; t++) { // two curling tendrils from the centre
      px(tile, 8 + t - 1, 8 - ((t * t) >> 3), 60, 214, 224);
      px(tile, 8 - t + 1, 8 - ((t * t) >> 3), 60, 214, 224);
    }
    for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) px(tile, 8 + dx, 9 + dy, 150, 244, 250); // glowing core
    if (framed) for (let i = 0; i < 16; i++) { px(tile, i, 0, 150, 96, 214); px(tile, i, 15, 150, 96, 214); px(tile, 0, i, 150, 96, 214); px(tile, 15, i, 150, 96, 214); } // amethyst frame
  }

  // Suspicious sand/gravel — the base grain speckled with buried-find flecks (M41).
  rand = mulberry32(4103);
  noisyFill(M41T.SUSPICIOUS_SAND, 214, 202, 150, 0.12, rand);
  for (let i = 0; i < 8; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M41T.SUSPICIOUS_SAND, x, y, 150, 130, 92); } // hint of a buried find
  noisyFill(M41T.SUSPICIOUS_GRAVEL, 130, 124, 118, 0.16, rand);
  for (let i = 0; i < 8; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M41T.SUSPICIOUS_GRAVEL, x, y, 84, 78, 72); }

  // Torchflower crop — a green sprout young, an orange-topped bloom mature (M44).
  for (let y = 9; y <= 15; y++) { const x = 7 + (y % 2); px(M44T.TORCHFLOWER_YOUNG, x, y, 90, 150, 78); px(M44T.TORCHFLOWER_YOUNG, x + 1, y, 110, 170, 92); } // low sprout
  for (let y = 3; y <= 15; y++) { px(M44T.TORCHFLOWER_MATURE, 7, y, 78, 138, 70); px(M44T.TORCHFLOWER_MATURE, 8, y, 96, 158, 84); } // tall stem
  for (let y = 2; y <= 5; y++) for (let x = 5; x <= 10; x++) if (Math.abs(x - 7.5) + Math.abs(y - 3.5) < 3.5) px(M44T.TORCHFLOWER_MATURE, x, y, 232, 140, 44); // orange bloom
  for (const [x, y] of [[7, 3], [8, 3]] as const) px(M44T.TORCHFLOWER_MATURE, x, y, 60, 40, 30); // dark centre

  // Pitcher crop — a small green shoot young, a broad blue-green pitcher mature (M45).
  for (let y = 11; y <= 15; y++) { px(M45T.PITCHER_YOUNG, 7, y, 74, 130, 84); px(M45T.PITCHER_YOUNG, 8, y, 92, 150, 100); } // shoot
  for (let y = 5; y <= 15; y++) { px(M45T.PITCHER_MATURE, 7, y, 62, 118, 96); px(M45T.PITCHER_MATURE, 8, y, 80, 138, 112); } // stalk
  for (let y = 3; y <= 8; y++) for (let x = 5; x <= 10; x++) if (Math.abs(x - 7.5) < (y - 2) * 0.7) px(M45T.PITCHER_MATURE, x, y, 92, 150, 150); // pitcher cup
  for (let x = 6; x <= 9; x++) px(M45T.PITCHER_MATURE, x, 3, 150, 90, 140); // magenta lip

  // Pink petals — scattered pink blossoms on a transparent ground tile (M45).
  rand = mulberry32(4501);
  for (let i = 0; i < 26; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M46T.PINK_PETALS, x, y, 236, 154, 196); px(M46T.PINK_PETALS, x, y + 1 < 16 ? y + 1 : y, 210, 120, 170); } // petal flecks

  // Nether biome ground (M47). Nylium = fungal crust over netherrack: a saturated
  // top, a speckled fade on the sides. Soul soil is a browner, softer soul sand.
  // Basalt is a dark grey column with vertical striations; blackstone is near-black.
  rand = mulberry32(4701);
  noisyFill(M47T.CRIMSON_NYLIUM_TOP, 139, 35, 35, 0.2, rand);
  for (let i = 0; i < 30; i++) px(M47T.CRIMSON_NYLIUM_TOP, (rand() * 16) | 0, (rand() * 16) | 0, 186, 60, 52);
  noisyFill(M47T.CRIMSON_NYLIUM_SIDE, 116, 45, 42, 0.22, rand);
  for (let x = 0; x < 16; x++) for (let y = 0; y < 4 + ((rand() * 3) | 0); y++) px(M47T.CRIMSON_NYLIUM_SIDE, x, y, 150, 40, 38); // crust along the top edge
  noisyFill(M47T.WARPED_NYLIUM_TOP, 22, 117, 115, 0.2, rand);
  for (let i = 0; i < 30; i++) px(M47T.WARPED_NYLIUM_TOP, (rand() * 16) | 0, (rand() * 16) | 0, 45, 160, 150);
  noisyFill(M47T.WARPED_NYLIUM_SIDE, 92, 52, 48, 0.22, rand);
  for (let x = 0; x < 16; x++) for (let y = 0; y < 4 + ((rand() * 3) | 0); y++) px(M47T.WARPED_NYLIUM_SIDE, x, y, 30, 128, 122);
  noisyFill(M47T.SOUL_SOIL, 76, 58, 44, 0.24, rand);
  for (let i = 0; i < 18; i++) px(M47T.SOUL_SOIL, (rand() * 16) | 0, (rand() * 16) | 0, 56, 42, 32); // hollows
  noisyFill(M47T.BASALT_SIDE, 75, 74, 80, 0.16, rand);
  for (const x of [2, 5, 6, 9, 12, 13]) for (let y = 0; y < 16; y++) px(M47T.BASALT_SIDE, x, y, 58, 57, 64); // vertical striations
  noisyFill(M47T.BASALT_TOP, 66, 65, 71, 0.16, rand);
  for (let i = 0; i < 22; i++) px(M47T.BASALT_TOP, (rand() * 16) | 0, (rand() * 16) | 0, 88, 87, 94); // fractured cap
  noisyFill(M47T.BLACKSTONE, 43, 37, 41, 0.2, rand);
  for (let i = 0; i < 20; i++) px(M47T.BLACKSTONE, (rand() * 16) | 0, (rand() * 16) | 0, 60, 52, 58);

  // Nether flora (M48): fungal stems, wart-block canopies, glowing shroomlight,
  // and the small cross-shaped fungi/roots that carpet a nylium forest floor.
  rand = mulberry32(4801);
  noisyFill(M48T.CRIMSON_STEM_SIDE, 93, 43, 54, 0.16, rand);
  for (const x of [3, 4, 9, 11]) for (let y = 0; y < 16; y++) px(M48T.CRIMSON_STEM_SIDE, x, y, 74, 32, 44); // bark grooves
  noisyFill(M48T.CRIMSON_STEM_END, 148, 88, 60, 0.16, rand);
  noisyFill(M48T.WARPED_STEM_SIDE, 43, 108, 106, 0.16, rand);
  for (const x of [3, 4, 9, 11]) for (let y = 0; y < 16; y++) px(M48T.WARPED_STEM_SIDE, x, y, 30, 84, 84);
  noisyFill(M48T.WARPED_STEM_END, 148, 130, 74, 0.16, rand);
  noisyFill(M48T.NETHER_WART_BLOCK, 115, 22, 17, 0.22, rand);
  for (let i = 0; i < 40; i++) px(M48T.NETHER_WART_BLOCK, (rand() * 16) | 0, (rand() * 16) | 0, 150, 34, 28); // warty lumps
  noisyFill(M48T.WARPED_WART_BLOCK, 22, 126, 134, 0.22, rand);
  for (let i = 0; i < 40; i++) px(M48T.WARPED_WART_BLOCK, (rand() * 16) | 0, (rand() * 16) | 0, 40, 160, 160);
  noisyFill(M48T.SHROOMLIGHT, 240, 160, 75, 0.14, rand);
  for (let i = 0; i < 26; i++) px(M48T.SHROOMLIGHT, (rand() * 16) | 0, (rand() * 16) | 0, 255, 214, 130); // hot pores
  // Fungi: a stubby stalk under a domed cap, on a transparent cross tile.
  const fungus = (t: number, cr: number, cg: number, cb: number): void => {
    for (let y = 9; y <= 15; y++) { px(t, 7, y, 214, 200, 176); px(t, 8, y, 190, 176, 152); } // pale stalk
    for (let y = 4; y <= 9; y++) for (let x = 4; x <= 11; x++) if (Math.abs(x - 7.5) < (10 - y) * 1.1) px(t, x, y, cr, cg, cb);
    for (let x = 5; x <= 10; x++) px(t, x, 4, (cr * 0.7) | 0, (cg * 0.7) | 0, (cb * 0.7) | 0); // cap shade
  };
  fungus(M48T.CRIMSON_FUNGUS, 178, 46, 40);
  fungus(M48T.WARPED_FUNGUS, 30, 150, 142);
  // Roots: a spray of thin fronds rising from the ground line.
  const roots = (t: number, cr: number, cg: number, cb: number): void => {
    for (const x of [3, 5, 7, 8, 10, 12]) for (let y = 8 + ((x * 5) % 4); y <= 15; y++) px(t, x, y, cr, cg, cb);
    for (const x of [4, 6, 9, 11]) for (let y = 11; y <= 15; y++) px(t, x, y, (cr * 0.75) | 0, (cg * 0.75) | 0, (cb * 0.75) | 0);
  };
  roots(M48T.CRIMSON_ROOTS, 168, 56, 78);
  roots(M48T.WARPED_ROOTS, 44, 158, 130);

  // Nether masonry (M49): fortress nether brick, and the bastion's polished and
  // gold-flecked blackstone.
  rand = mulberry32(4901);
  noisyFill(M49T.NETHER_BRICKS, 45, 22, 29, 0.14, rand);
  for (const y of [3, 7, 11, 15]) for (let x = 0; x < 16; x++) px(M49T.NETHER_BRICKS, x, y, 28, 13, 18); // courses
  for (let y = 0; y < 16; y++) { const x = (y >> 2) % 2 ? 3 : 11; px(M49T.NETHER_BRICKS, x, y, 28, 13, 18); } // staggered joints
  noisyFill(M49T.POLISHED_BLACKSTONE, 49, 43, 50, 0.1, rand);
  for (let x = 0; x < 16; x++) { px(M49T.POLISHED_BLACKSTONE, x, 7, 36, 31, 37); px(M49T.POLISHED_BLACKSTONE, x, 15, 36, 31, 37); }
  for (let y = 0; y < 16; y++) px(M49T.POLISHED_BLACKSTONE, 7, y, 36, 31, 37);
  noisyFill(M49T.GILDED_BLACKSTONE, 43, 37, 41, 0.18, rand);
  for (let i = 0; i < 14; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M49T.GILDED_BLACKSTONE, x, y, 232, 186, 74); px(M49T.GILDED_BLACKSTONE, x, y + 1 < 16 ? y + 1 : y, 190, 148, 52); } // gold flecks

  // The End (M50): pale speckled end stone, the portal's starfield void, and the
  // frame block with and without its eye of ender.
  rand = mulberry32(5001);
  noisyFill(M50T.END_STONE, 219, 223, 158, 0.12, rand);
  for (let i = 0; i < 24; i++) px(M50T.END_STONE, (rand() * 16) | 0, (rand() * 16) | 0, 196, 200, 130); // grain
  noisyFill(M50T.END_PORTAL, 12, 8, 34, 0.3, rand);
  for (let i = 0; i < 22; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M50T.END_PORTAL, x, y, 190, 200, 255); } // starfield
  for (let i = 0; i < 10; i++) px(M50T.END_PORTAL, (rand() * 16) | 0, (rand() * 16) | 0, 120, 90, 210); // violet haze
  noisyFill(M50T.END_PORTAL_FRAME_SIDE, 76, 139, 111, 0.12, rand);
  for (let y = 11; y < 16; y++) for (let x = 0; x < 16; x++) px(M50T.END_PORTAL_FRAME_SIDE, x, y, 56, 104, 84); // base course
  noisyFill(M50T.END_PORTAL_FRAME_TOP, 104, 164, 134, 0.12, rand);
  for (let x = 3; x <= 12; x++) for (let y = 3; y <= 12; y++) px(M50T.END_PORTAL_FRAME_TOP, x, y, 70, 128, 104); // empty socket
  noisyFill(M50T.END_PORTAL_FRAME_EYE, 104, 164, 134, 0.12, rand);
  for (let x = 3; x <= 12; x++) for (let y = 3; y <= 12; y++) px(M50T.END_PORTAL_FRAME_EYE, x, y, 40, 96, 92); // socket
  for (let x = 4; x <= 11; x++) for (let y = 4; y <= 11; y++) px(M50T.END_PORTAL_FRAME_EYE, x, y, 106, 190, 156); // the eye
  for (let x = 6; x <= 9; x++) for (let y = 6; y <= 9; y++) px(M50T.END_PORTAL_FRAME_EYE, x, y, 32, 42, 70); // pupil

  // End city + chorus flora (M51): purpur masonry, the glowing end rod, and the
  // violet chorus stalk/bloom.
  rand = mulberry32(5101);
  noisyFill(M51T.PURPUR, 169, 127, 169, 0.1, rand);
  for (let i = 0; i < 28; i++) px(M51T.PURPUR, (rand() * 16) | 0, (rand() * 16) | 0, 148, 108, 150); // mottling
  noisyFill(M51T.PURPUR_PILLAR_SIDE, 171, 132, 171, 0.09, rand);
  for (const x of [1, 6, 9, 14]) for (let y = 0; y < 16; y++) px(M51T.PURPUR_PILLAR_SIDE, x, y, 142, 104, 144); // flutes
  noisyFill(M51T.PURPUR_PILLAR_END, 182, 146, 182, 0.09, rand);
  noisyFill(M51T.END_STONE_BRICKS, 218, 224, 164, 0.1, rand);
  for (const y of [3, 7, 11, 15]) for (let x = 0; x < 16; x++) px(M51T.END_STONE_BRICKS, x, y, 176, 184, 124); // courses
  for (let y = 0; y < 16; y++) { const x = (y >> 2) % 2 ? 3 : 11; px(M51T.END_STONE_BRICKS, x, y, 176, 184, 124); }
  for (let y = 0; y < 16; y++) for (let x = 6; x <= 9; x++) px(M51T.END_ROD, x, y, 229, 227, 216); // pale rod
  for (let y = 0; y < 16; y++) { px(M51T.END_ROD, 6, y, 246, 246, 240); px(M51T.END_ROD, 9, y, 198, 194, 186); }
  for (let x = 5; x <= 10; x++) for (let y = 0; y <= 2; y++) px(M51T.END_ROD, x, y, 240, 214, 246); // glowing tip
  noisyFill(M51T.CHORUS_PLANT, 92, 63, 92, 0.16, rand);
  for (let i = 0; i < 22; i++) px(M51T.CHORUS_PLANT, (rand() * 16) | 0, (rand() * 16) | 0, 118, 84, 118); // knobbly stalk
  noisyFill(M51T.CHORUS_FLOWER, 154, 127, 154, 0.12, rand);
  for (let x = 4; x <= 11; x++) for (let y = 4; y <= 11; y++) px(M51T.CHORUS_FLOWER, x, y, 206, 190, 206); // pale bloom face
  for (let x = 6; x <= 9; x++) for (let y = 6; y <= 9; y++) px(M51T.CHORUS_FLOWER, x, y, 120, 88, 122); // dark centre

  // Lush caves (M52): moss, the faintly luminous lichen crust, and cave vines with
  // and without their glow berries.
  rand = mulberry32(5201);
  noisyFill(M52T.MOSS, 90, 122, 46, 0.26, rand);
  for (let i = 0; i < 34; i++) px(M52T.MOSS, (rand() * 16) | 0, (rand() * 16) | 0, 68, 100, 34); // tufts
  for (let i = 0; i < 46; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M52T.GLOW_LICHEN, x, y, 124, 152, 122); px(M52T.GLOW_LICHEN, (x + 1) % 16, y, 96, 128, 100); } // sparse crust
  for (let y = 0; y < 16; y++) { px(M52T.CAVE_VINES, 7, y, 82, 116, 56); px(M52T.CAVE_VINES, 8, y, 64, 96, 44); } // hanging strand
  for (const [x, y] of [[5, 3], [10, 6], [4, 10], [11, 13]] as const) px(M52T.CAVE_VINES, x, y, 74, 108, 50); // stray leaves
  for (let y = 0; y < 16; y++) { px(M52T.CAVE_VINES_BERRIES, 7, y, 82, 116, 56); px(M52T.CAVE_VINES_BERRIES, 8, y, 64, 96, 44); }
  for (const [x, y] of [[5, 4], [10, 7], [4, 11], [11, 14], [6, 9]] as const) { px(M52T.CAVE_VINES_BERRIES, x, y, 246, 190, 76); px(M52T.CAVE_VINES_BERRIES, x, y + 1 < 16 ? y + 1 : y, 214, 154, 52); } // glow berries

  // Pink petals at amounts 1..3 (M53) — the same blossom flecks, thinned out, so a
  // single petal reads differently from a carpet (amount 4 keeps the M46 tile).
  for (let n = 1; n <= 3; n++) {
    rand = mulberry32(5300 + n);
    const flecks = [5, 12, 19][n - 1]!;
    for (let i = 0; i < flecks; i++) {
      const x = (rand() * 16) | 0, y = (rand() * 16) | 0;
      px(M53T.PETALS_1 + (n - 1), x, y, 236, 154, 196);
      px(M53T.PETALS_1 + (n - 1), x, y + 1 < 16 ? y + 1 : y, 210, 120, 170);
    }
  }

  // End gateway (M54) — the exit portal's starfield, but shot through with the
  // teal beam that marks it as the jump OUT to the shards rather than the way home.
  rand = mulberry32(5401);
  noisyFill(M54T.END_GATEWAY, 8, 6, 26, 0.3, rand);
  for (let i = 0; i < 26; i++) px(M54T.END_GATEWAY, (rand() * 16) | 0, (rand() * 16) | 0, 200, 214, 255); // starfield
  for (let y = 0; y < 16; y++) for (let x = 6; x <= 9; x++) px(M54T.END_GATEWAY, x, y, 62, 168, 158); // teal beam
  for (let y = 0; y < 16; y++) { px(M54T.END_GATEWAY, 7, y, 132, 232, 214); px(M54T.END_GATEWAY, 8, y, 132, 232, 214); } // bright core

  // Nether vines (M55) — weeping hangs crimson off a canopy, twisting climbs teal
  // out of the nylium, and sprouts are the warped forest's short ground fuzz.
  rand = mulberry32(5501);
  for (let y = 0; y < 16; y++) { px(M55T.WEEPING_VINES, 7, y, 152, 30, 44); px(M55T.WEEPING_VINES, 8, y, 116, 22, 36); } // strand
  for (const [x, y] of [[5, 2], [10, 5], [4, 9], [11, 12], [6, 14]] as const) { px(M55T.WEEPING_VINES, x, y, 178, 44, 58); px(M55T.WEEPING_VINES, x, y + 1 < 16 ? y + 1 : y, 138, 28, 40); } // droplets
  for (let y = 0; y < 16; y++) { px(M55T.TWISTING_VINES, 7, y, 26, 132, 118); px(M55T.TWISTING_VINES, 8, y, 20, 104, 94); } // stalk
  for (const [x, y] of [[5, 3], [10, 6], [4, 10], [11, 13]] as const) { px(M55T.TWISTING_VINES, x, y, 40, 168, 150); px(M55T.TWISTING_VINES, x, y + 1 < 16 ? y + 1 : y, 26, 132, 118); } // curls
  for (let i = 0; i < 26; i++) { // sprouts: short teal tufts rooted along the bottom
    const x = (rand() * 16) | 0, h = 3 + ((rand() * 4) | 0);
    for (let y = 15; y > 15 - h; y--) px(M55T.NETHER_SPROUTS, x, y, 32 + ((rand() * 40) | 0), 150, 134);
  }

  // Lush-cave depth (M56) — the garden dressing under a lush pocket.
  rand = mulberry32(5601);
  noisyFill(M56T.ROOTED_DIRT, 122, 92, 64, 0.16, rand); // dirt shot through with pale roots
  for (let i = 0; i < 30; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M56T.ROOTED_DIRT, x, y, 176, 142, 96); px(M56T.ROOTED_DIRT, x, (y + 1) % 16, 152, 118, 78); }
  for (let i = 0; i < 7; i++) { // roots: pale strands hanging from the top edge
    const x = 1 + ((rand() * 14) | 0), h = 6 + ((rand() * 9) | 0);
    for (let y = 0; y < h; y++) px(M56T.HANGING_ROOTS, x, y, 186, 152, 108);
  }
  for (let y = 0; y < 8; y++) { px(M56T.SPORE_BLOSSOM, 7, y, 96, 132, 70); px(M56T.SPORE_BLOSSOM, 8, y, 78, 112, 56); } // stem
  for (let x = 3; x <= 12; x++) for (let y = 8; y <= 12; y++) px(M56T.SPORE_BLOSSOM, x, y, 220, 118, 166); // pink bloom
  for (let x = 6; x <= 9; x++) for (let y = 9; y <= 11; y++) px(M56T.SPORE_BLOSSOM, x, y, 246, 172, 206); // bright centre
  for (let x = 5; x <= 10; x++) for (let y = 4; y <= 9; y++) px(M56T.SMALL_DRIPLEAF, x, y, 96, 158, 62); // small pad
  for (let y = 9; y < 16; y++) { px(M56T.SMALL_DRIPLEAF, 7, y, 76, 122, 52); px(M56T.SMALL_DRIPLEAF, 8, y, 76, 122, 52); } // stalk
  for (let x = 1; x <= 14; x++) for (let y = 2; y <= 8; y++) px(M56T.BIG_DRIPLEAF, x, y, 106, 172, 66); // broad pad
  for (let x = 1; x <= 14; x++) px(M56T.BIG_DRIPLEAF, x, 8, 70, 118, 44); // shaded lip
  for (let y = 9; y < 16; y++) { px(M56T.BIG_DRIPLEAF, 7, y, 82, 132, 56); px(M56T.BIG_DRIPLEAF, 8, y, 82, 132, 56); } // thick stalk
  for (let i = 0; i < 60; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M56T.AZALEA, x, y, 66 + ((rand() * 46) | 0), 128 + ((rand() * 40) | 0), 44); } // bushy leaves
  for (let i = 0; i < 60; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M56T.FLOWERING_AZALEA, x, y, 66 + ((rand() * 46) | 0), 128 + ((rand() * 40) | 0), 44); }
  for (let i = 0; i < 16; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M56T.FLOWERING_AZALEA, x, y, 216, 128, 196); } // its blossoms

  // Per-sherd decorated-pot faces (M57): the same terracotta body as M42, but each
  // of the four sides wears the motif of the sherd it was crafted from — the whole
  // reason a decorated pot is decorated.
  const potBody = (tile: number, seed: number): void => {
    rand = mulberry32(seed);
    noisyFill(tile, 184, 116, 90, 0.1, rand);
    for (let y = 3; y <= 12; y++) { px(tile, 3, y, 150, 90, 68); px(tile, 12, y, 150, 90, 68); }
    for (let x = 4; x <= 11; x++) { px(tile, x, 3, 150, 90, 68); px(tile, x, 12, 150, 90, 68); }
  };
  /** Scratch a motif into a pot face from a list of incised cells. */
  const incise = (tile: number, cells: readonly (readonly [number, number])[]): void => {
    for (const [x, y] of cells) px(tile, x, y, 116, 62, 44);
  };
  potBody(M57T.POT_ANGLER, 5701);
  incise(M57T.POT_ANGLER, [[6, 5], [7, 5], [8, 6], [8, 7], [7, 8], [6, 8], [6, 9], [7, 10], [9, 10], [10, 9], [10, 7]]); // rod + line + fish
  potBody(M57T.POT_ARCHER, 5702);
  incise(M57T.POT_ARCHER, [[6, 5], [6, 6], [6, 7], [6, 8], [6, 9], [7, 6], [7, 9], [8, 7], [9, 7], [10, 7]]); // drawn bow
  potBody(M57T.POT_ARMS_UP, 5703);
  incise(M57T.POT_ARMS_UP, [[7, 5], [8, 5], [7, 6], [8, 6], [5, 5], [5, 6], [10, 5], [10, 6], [6, 7], [9, 7], [7, 8], [8, 8], [7, 9], [8, 9], [7, 10], [8, 10]]); // figure, arms raised
  potBody(M57T.POT_SKULL, 5704);
  incise(M57T.POT_SKULL, [[6, 5], [7, 5], [8, 5], [9, 5], [5, 6], [10, 6], [5, 7], [10, 7], [6, 7], [9, 7], [5, 8], [10, 8], [6, 9], [7, 9], [8, 9], [9, 9], [7, 10], [8, 10]]); // skull
  rand = mulberry32(5705);
  noisyFill(M57T.POT_TOP, 168, 102, 78, 0.1, rand);
  for (let x = 4; x <= 11; x++) for (let y = 4; y <= 11; y++) px(M57T.POT_TOP, x, y, 96, 54, 40); // the open mouth

  // The flower family (M58). Each is a stem with its own bloom shape and colour —
  // a recolour of one silhouette would defeat the point of telling them apart.
  const FLOWER_ART: Record<string, { petal: [number, number, number]; heart?: [number, number, number]; wide?: boolean; tall?: boolean }> = {
    allium: { petal: [181, 126, 220], heart: [140, 92, 176], wide: true },
    azure_bluet: { petal: [232, 236, 224], heart: [232, 200, 92] },
    blue_orchid: { petal: [42, 176, 224], heart: [232, 236, 120], wide: true },
    cornflower: { petal: [91, 110, 225], heart: [58, 70, 168], tall: true },
    lily_of_the_valley: { petal: [240, 244, 238], tall: true },
    oxeye_daisy: { petal: [242, 242, 228], heart: [236, 200, 76], wide: true },
    orange_tulip: { petal: [229, 138, 43], tall: true },
    pink_tulip: { petal: [235, 163, 200], tall: true },
    red_tulip: { petal: [212, 59, 50], tall: true },
    white_tulip: { petal: [238, 240, 234], tall: true },
  };
  for (const name of FLOWERS) {
    const tile = flowerTileOf(name)!;
    const art = FLOWER_ART[name]!;
    rand = mulberry32(5800 + tile);
    // Stem, always: two dark-green columns from the base up to the bloom.
    const bloomBase = art.tall ? 7 : 6;
    for (let y = bloomBase; y < 16; y++) { px(tile, 7, y, 62, 108, 44); px(tile, 8, y, 48, 90, 36); }
    for (const [x, y] of [[6, 10], [9, 12]] as const) px(tile, x, y, 70, 122, 50); // leaves
    // Bloom: a wide head, a tall cup, or a small round one.
    const [pr, pg, pb] = art.petal;
    const x0 = art.wide ? 4 : 5, x1 = art.wide ? 11 : 10;
    const y0 = 2, y1 = art.tall ? bloomBase - 1 : bloomBase;
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) px(tile, x, y, pr, pg, pb);
    if (art.heart) {
      const [hr, hg, hb] = art.heart;
      for (let x = 7; x <= 8; x++) for (let y = y0 + 1; y <= Math.max(y0 + 1, y1 - 1); y++) px(tile, x, y, hr, hg, hb);
    }
    // A couple of shaded petal edges so the head reads as a flower, not a swatch.
    for (let i = 0; i < 4; i++) {
      px(tile, x0 + ((rand() * (x1 - x0 + 1)) | 0), y1, pr * 0.78, pg * 0.78, pb * 0.78);
    }
  }

  // Netherite's chain (M59). Ancient debris is netherrack-dark with a scoured
  // metallic grain — the top face shows the cross-section, the sides the rock.
  rand = mulberry32(5900);
  noisyFill(M59T.ANCIENT_DEBRIS_SIDE, 92, 66, 56, 0.16, rand);
  for (let i = 0; i < 26; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M59T.ANCIENT_DEBRIS_SIDE, x, y, 58, 44, 44); }
  noisyFill(M59T.ANCIENT_DEBRIS_TOP, 84, 60, 52, 0.14, rand);
  for (let x = 3; x <= 12; x++) for (let y = 6; y <= 9; y++) px(M59T.ANCIENT_DEBRIS_TOP, x, y, 60, 48, 50); // the exposed seam
  for (let x = 4; x <= 11; x++) px(M59T.ANCIENT_DEBRIS_TOP, x, 7, 116, 100, 104); // its metallic glint
  noisyFill(M59T.NETHERITE_BLOCK, 70, 62, 66, 0.10, rand);
  for (let i = 0; i < 20; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M59T.NETHERITE_BLOCK, x, y, 100, 92, 96); } // hammered highlights
  for (let x = 0; x < 16; x++) { px(M59T.NETHERITE_BLOCK, x, 0, 46, 40, 44); px(M59T.NETHERITE_BLOCK, 0, x, 46, 40, 44); } // bevel
  noisyFill(M59T.NETHER_GOLD_ORE, 124, 48, 42, 0.16, rand); // netherrack body
  for (let i = 0; i < 18; i++) {
    const x = 1 + ((rand() * 14) | 0), y = 1 + ((rand() * 14) | 0);
    px(M59T.NETHER_GOLD_ORE, x, y, 246, 208, 92); px(M59T.NETHER_GOLD_ORE, x + 1, y, 204, 164, 60); // gold flecks
  }
  // Smithing table: a dark slate top over an oak frame, tools laid out on the front.
  rand = mulberry32(5901);
  noisyFill(M59T.SMITHING_SIDE, 92, 68, 46, 0.10, rand);
  for (let x = 0; x < 16; x++) for (let y = 0; y <= 3; y++) px(M59T.SMITHING_SIDE, x, y, 58, 62, 76); // slate lip
  noisyFill(M59T.SMITHING_TOP, 60, 64, 78, 0.12, rand);
  for (let x = 2; x <= 13; x++) for (let y = 2; y <= 13; y++) px(M59T.SMITHING_TOP, x, y, 74, 78, 92); // worked surface
  noisyFill(M59T.SMITHING_FRONT, 92, 68, 46, 0.10, rand);
  for (let x = 0; x < 16; x++) for (let y = 0; y <= 3; y++) px(M59T.SMITHING_FRONT, x, y, 58, 62, 76);
  for (let y = 6; y <= 12; y++) px(M59T.SMITHING_FRONT, 4, y, 168, 168, 176); // a hammer haft…
  for (let x = 2; x <= 6; x++) px(M59T.SMITHING_FRONT, x, 5, 120, 120, 128); // …and its head
  for (let y = 6; y <= 12; y++) { px(M59T.SMITHING_FRONT, 10, y, 148, 106, 62); px(M59T.SMITHING_FRONT, 11, y, 120, 84, 48); } // a second tool

  // Iron bars (M60): a cutout tile — vertical bars with a lip top and bottom, the
  // gaps left fully transparent so a stronghold window reads through.
  for (const x of [3, 4, 11, 12]) for (let y = 0; y < 16; y++) px(M60T.IRON_BARS, x, y, 148, 148, 156);
  for (const x of [7, 8]) for (let y = 0; y < 16; y++) px(M60T.IRON_BARS, x, y, 168, 168, 176);
  for (const y of [0, 1, 14, 15]) for (let x = 0; x < 16; x++) px(M60T.IRON_BARS, x, y, 120, 120, 128); // lips

  // Decorated pot — a terracotta body with a scratched sherd motif face (M42).
  rand = mulberry32(4201);
  noisyFill(M42T.DECORATED_POT, 184, 116, 90, 0.1, rand);
  for (let y = 3; y <= 12; y++) { px(M42T.DECORATED_POT, 3, y, 150, 90, 68); px(M42T.DECORATED_POT, 12, y, 150, 90, 68); } // pot edges
  for (let x = 4; x <= 11; x++) { px(M42T.DECORATED_POT, x, 3, 150, 90, 68); px(M42T.DECORATED_POT, x, 12, 150, 90, 68); } // rim + base
  for (const [x, y] of [[7, 6], [8, 6], [6, 8], [9, 8], [7, 10], [8, 10]] as const) px(M42T.DECORATED_POT, x, y, 120, 66, 48); // incised motif

  // End crystal — a magenta faceted core in a dark cage; dragon egg is a glossy
  //  purple-black ovoid (M21).
  rand = mulberry32(2101);
  noisyFill(M21T.END_CRYSTAL, 40, 24, 54, 0.14, rand);
  for (let y = 4; y <= 12; y++) { const half = y < 8 ? y - 3 : 12 - y; for (let x = 8 - half; x <= 8 + half; x++) px(M21T.END_CRYSTAL, x, y, 214, 150, 244); } // faceted core
  for (let x = 3; x <= 12; x++) { px(M21T.END_CRYSTAL, x, 3, 20, 14, 28); px(M21T.END_CRYSTAL, x, 12, 20, 14, 28); } // cage bars
  rand = mulberry32(2102);
  noisyFill(M21T.DRAGON_EGG, 18, 12, 26, 0.10, rand);
  for (let y = 2; y <= 13; y++) for (let x = 0; x < TILE_PX; x++) {
    const rx = (x - 7.5) / 5.5, ry = (y - 7.5) / 6.5;
    if (rx * rx + ry * ry <= 1) px(M21T.DRAGON_EGG, x, y, 34 + (x < 8 ? 22 : 0), 20, 46 + (y < 7 ? 26 : 0)); // glossy ovoid w/ a highlight
  }
  // Trial spawner — a dark tuff cube with a glowing amber cage face; the vault is a
  //  copper-framed slot with a teal keyhole (M22).
  rand = mulberry32(2220);
  noisyFill(M22C.TRIAL_SPAWNER, 44, 48, 54, 0.14, rand);
  for (let g = 3; g <= 12; g += 3) for (let x = 3; x <= 12; x++) { px(M22C.TRIAL_SPAWNER, x, g, 214, 150, 40); px(M22C.TRIAL_SPAWNER, g, x, 214, 150, 40); } // amber cage
  for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) px(M22C.TRIAL_SPAWNER, 8 + dx, 8 + dy, 250, 200, 90); // glowing core
  rand = mulberry32(2221);
  noisyFill(M22C.VAULT, 46, 58, 64, 0.12, rand);
  for (let x = 2; x <= 13; x++) { px(M22C.VAULT, x, 2, 176, 116, 70); px(M22C.VAULT, x, 13, 176, 116, 70); } // copper frame
  for (let y = 2; y <= 13; y++) { px(M22C.VAULT, 2, y, 176, 116, 70); px(M22C.VAULT, 13, y, 176, 116, 70); }
  for (let y = 5; y <= 10; y++) px(M22C.VAULT, 8, y, 46, 200, 200); // teal keyhole slot
  px(M22C.VAULT, 7, 6, 46, 200, 200); px(M22C.VAULT, 9, 6, 46, 200, 200);

  // Dripstone — a tapered brown spike billboard + a pitted brown block (M19).
  rand = mulberry32(1901);
  for (let y = 0; y < TILE_PX; y++) { const half = Math.max(0, Math.round((y / TILE_PX) * 3.5)); for (let x = 8 - half; x <= 7 + half; x++) { const edge = x === 8 - half || x === 7 + half; px(M19B.POINTED_DRIPSTONE, x, y, edge ? 96 : 138, edge ? 74 : 106, edge ? 58 : 82); } } // downward taper
  rand = mulberry32(1902);
  noisyFill(M19B.DRIPSTONE_BLOCK, 130, 98, 76, 0.16, rand);
  for (let i = 0; i < 20; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M19B.DRIPSTONE_BLOCK, x, y, 96, 70, 54); } // pitted pocks

  // Respawn anchor — dark crying-obsidian body with a glowing magenta charge core (M20).
  rand = mulberry32(2030);
  noisyFill(M20B.RESPAWN_ANCHOR, 28, 20, 40, 0.14, rand);
  for (let i = 0; i < 12; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(M20B.RESPAWN_ANCHOR, x, y, 40, 90, 150); } // crying-obsidian flecks
  for (let y = 5; y <= 10; y++) for (let x = 5; x <= 10; x++) { const edge = x === 5 || x === 10 || y === 5 || y === 10; px(M20B.RESPAWN_ANCHOR, x, y, edge ? 120 : 214, edge ? 40 : 90, edge ? 150 : 214); } // charge core

  // ── Wood species (MB.1). Each gets a planed-plank tile (grain grooves) and a
  //    sapling; the six new species also get bark + end-grain + leaf tiles
  //    (spruce/birch reuse their existing VEG log/leaf tiles). ──
  let woodSeed = 3100;
  for (const sp of WOOD_SPECIES) {
    const pal = WOOD_PALETTE[sp];
    const tiles = WOOD_TILES[sp];
    // Planks: horizontal boards with darker seams every 5 px.
    rand = mulberry32(woodSeed++);
    noisyFill(tiles.plank, pal.plank[0], pal.plank[1], pal.plank[2], 0.12, rand);
    for (let y = 3; y < TILE_PX; y += 5) for (let x = 0; x < TILE_PX; x++) px(tiles.plank, x, y, pal.plank[0] * 0.72, pal.plank[1] * 0.72, pal.plank[2] * 0.72);
    // Sapling: a small cross-billboard — green (or pink) leaf blob over a stem.
    rand = mulberry32(woodSeed++);
    ctx.clearRect(tiles.sapling * TILE_PX, 0, TILE_PX, TILE_PX);
    for (let y = 9; y < TILE_PX; y++) px(tiles.sapling, 8, y, 96, 72, 40); // stem
    for (let y = 2; y < 11; y++) for (let x = 4; x < 12; x++) if (Math.abs(x - 8) + Math.abs(y - 6) < 5 && rand() > 0.2) px(tiles.sapling, x, y, pal.leaf[0], pal.leaf[1], pal.leaf[2]);
    if (sp === 'spruce' || sp === 'birch') continue; // log/leaf tiles already painted (VEG)
    // Bark: vertical streaks over the base bark colour.
    rand = mulberry32(woodSeed++);
    noisyFill(tiles.logSide, pal.bark[0], pal.bark[1], pal.bark[2], 0.18, rand);
    for (let x = 1; x < TILE_PX; x += 4) for (let y = 0; y < TILE_PX; y++) px(tiles.logSide, x, y, pal.bark[0] * 0.7, pal.bark[1] * 0.7, pal.bark[2] * 0.7);
    // End grain: concentric-ish rings on the lighter core colour.
    rand = mulberry32(woodSeed++);
    noisyFill(tiles.logEnd, pal.end[0], pal.end[1], pal.end[2], 0.1, rand);
    for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) { const d = Math.hypot(x - 8, y - 8); if ((d | 0) % 3 === 0) px(tiles.logEnd, x, y, pal.end[0] * 0.78, pal.end[1] * 0.78, pal.end[2] * 0.78); }
    // Leaves: cutout foliage with transparent holes.
    rand = mulberry32(woodSeed++);
    ctx.clearRect(tiles.leaves * TILE_PX, 0, TILE_PX, TILE_PX);
    for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) { if (rand() < 0.18) continue; const v = 0.7 + rand() * 0.6; px(tiles.leaves, x, y, pal.leaf[0] * v, pal.leaf[1] * v, pal.leaf[2] * v); }
  }

  // ── MB.2 farming: melon (green rind top + striped side), bamboo stalk, nether wart. ──
  rand = mulberry32(3300);
  noisyFill(MB2T.MELON_TOP, 60, 120, 44, 0.14, rand);
  noisyFill(MB2T.MELON_SIDE, 78, 128, 40, 0.12, rand);
  for (let x = 1; x < TILE_PX; x += 3) for (let y = 0; y < TILE_PX; y++) px(MB2T.MELON_SIDE, x, y, 44, 96, 30); // rind stripes
  rand = mulberry32(3301);
  ctx.clearRect(MB2T.BAMBOO * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let y = 0; y < TILE_PX; y++) for (let x = 6; x < 10; x++) { const v = 0.8 + rand() * 0.4; px(MB2T.BAMBOO, x, y, 120 * v, 168 * v, 70 * v); }
  for (const y of [4, 11]) for (let x = 6; x < 10; x++) px(MB2T.BAMBOO, x, y, 78, 112, 44); // node rings
  rand = mulberry32(3302);
  ctx.clearRect(MB2T.NETHER_WART * TILE_PX, 0, TILE_PX, TILE_PX);
  for (let y = 3; y < TILE_PX; y++) for (let x = 3; x < 13; x++) if (rand() > 0.4) px(MB2T.NETHER_WART, x, y, 128 + rand() * 40, 20, 24); // dark-red clusters

  // ── MB.1 pass 5 stone variants (mossy/cracked/chiseled/smooth + deepslate). ──
  const brickLines = (t: number, r: number, g: number, b: number): void => {
    for (let y = 0; y < TILE_PX; y += 4) for (let x = 0; x < TILE_PX; x++) px(t, x, y, r * 0.6, g * 0.6, b * 0.6);
    for (let y = 0; y < TILE_PX; y++) { const off = (y & 4) ? 8 : 0; px(t, (off) % TILE_PX, y, r * 0.6, g * 0.6, b * 0.6); px(t, (off + 8) % TILE_PX, y, r * 0.6, g * 0.6, b * 0.6); }
  };
  rand = mulberry32(5100);
  noisyFill(MB5T.MOSSY_COBBLESTONE, 122, 122, 122, 0.3, rand);
  for (let i = 0; i < 40; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(MB5T.MOSSY_COBBLESTONE, x, y, 70, 104, 54); }
  rand = mulberry32(5101);
  noisyFill(MB5T.MOSSY_STONE_BRICKS, 124, 124, 124, 0.14, rand); brickLines(MB5T.MOSSY_STONE_BRICKS, 124, 124, 124);
  for (let i = 0; i < 26; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(MB5T.MOSSY_STONE_BRICKS, x, y, 70, 104, 54); }
  rand = mulberry32(5102);
  noisyFill(MB5T.CRACKED_STONE_BRICKS, 122, 122, 122, 0.16, rand); brickLines(MB5T.CRACKED_STONE_BRICKS, 122, 122, 122);
  for (let i = 0; i < 3; i++) { let x = (rand() * 16) | 0; for (let y = (rand() * 6) | 0; y < TILE_PX; y++) { px(MB5T.CRACKED_STONE_BRICKS, x, y, 70, 70, 70); if (rand() > 0.6) x = Math.max(0, Math.min(15, x + (rand() > 0.5 ? 1 : -1))); } }
  rand = mulberry32(5103);
  noisyFill(MB5T.CHISELED_STONE_BRICKS, 126, 126, 126, 0.12, rand);
  for (let i = 2; i < 14; i++) { px(MB5T.CHISELED_STONE_BRICKS, 2, i, 84, 84, 84); px(MB5T.CHISELED_STONE_BRICKS, 13, i, 84, 84, 84); px(MB5T.CHISELED_STONE_BRICKS, i, 2, 84, 84, 84); px(MB5T.CHISELED_STONE_BRICKS, i, 13, 84, 84, 84); }
  rand = mulberry32(5104);
  noisyFill(MB5T.SMOOTH_STONE, 142, 142, 144, 0.06, rand);
  rand = mulberry32(5105);
  noisyFill(MB5T.COBBLED_DEEPSLATE, 74, 75, 80, 0.3, rand);
  for (let i = 0; i < 30; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(MB5T.COBBLED_DEEPSLATE, x, y, 58, 59, 64); }
  rand = mulberry32(5106);
  noisyFill(MB5T.POLISHED_DEEPSLATE, 70, 71, 76, 0.08, rand);
  rand = mulberry32(5107);
  noisyFill(MB5T.DEEPSLATE_BRICKS, 68, 69, 74, 0.12, rand); brickLines(MB5T.DEEPSLATE_BRICKS, 68, 69, 74);
  rand = mulberry32(5108);
  noisyFill(MB5T.DEEPSLATE_TILES, 62, 63, 68, 0.1, rand);
  for (let i = 0; i < TILE_PX; i++) { px(MB5T.DEEPSLATE_TILES, 7, i, 44, 45, 50); px(MB5T.DEEPSLATE_TILES, i, 7, 44, 45, 50); px(MB5T.DEEPSLATE_TILES, 15, i, 44, 45, 50); px(MB5T.DEEPSLATE_TILES, i, 15, 44, 45, 50); }

  // ── MB.1 pass 6 copper oxidation matrix. Four stages fade the base metal from
  //    bright orange to green patina; each higher stage scatters more teal
  //    flecks. `cut` variants add a grooved 2×2-panel overlay. ──
  const COPPER_BASE: [number, number, number][] = [
    [193, 108, 79], [140, 124, 96], [98, 148, 118], [82, 162, 132], // unaffected → oxidized
  ];
  const PATINA_FLECKS = [0, 22, 46, 30]; // stage → # of teal flecks scattered
  const copperTile = (t: number, stage: number, cut: boolean): void => {
    const [r, g, b] = COPPER_BASE[stage]!;
    noisyFill(t, r, g, b, stage === 0 ? 0.14 : 0.2, rand);
    for (let i = 0; i < PATINA_FLECKS[stage]!; i++) {
      const x = (rand() * 16) | 0, y = (rand() * 16) | 0;
      px(t, x, y, 92, 168, 138); // verdigris speckle
    }
    if (cut) { // grooved cross dividing the tile into four cut panels
      const dk = (Math.max(0, r - 40)), dg = Math.max(0, g - 40), db = Math.max(0, b - 40);
      for (let i = 0; i < TILE_PX; i++) { px(t, 7, i, dk, dg, db); px(t, 8, i, dk, dg, db); px(t, i, 7, dk, dg, db); px(t, i, 8, dk, dg, db); }
    }
  };
  copperTile(MB6T.COPPER_BLOCK, 0, false); copperTile(MB6T.EXPOSED_COPPER, 1, false);
  copperTile(MB6T.WEATHERED_COPPER, 2, false); copperTile(MB6T.OXIDIZED_COPPER, 3, false);
  copperTile(MB6T.CUT_COPPER, 0, true); copperTile(MB6T.EXPOSED_CUT_COPPER, 1, true);
  copperTile(MB6T.WEATHERED_CUT_COPPER, 2, true); copperTile(MB6T.OXIDIZED_CUT_COPPER, 3, true);

  // ── MB.3 pass 7 campfire (simplified lit cube). ──
  // Top: charred wood with glowing embers + flame flecks.
  noisyFill(MB7T.CAMPFIRE_TOP, 46, 34, 28, 0.3, rand);
  for (let i = 0; i < 34; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; const hot = rand(); px(MB7T.CAMPFIRE_TOP, x, y, hot > 0.6 ? 248 : 214, hot > 0.6 ? 176 : 96, hot > 0.6 ? 60 : 30); }
  for (const [x, y] of [[6, 5], [9, 7], [7, 10], [10, 4], [5, 9]] as const) px(MB7T.CAMPFIRE_TOP, x, y, 252, 224, 120); // bright flame tips
  // Side: oak-log bark darkened by soot, with the pale end-grain notch.
  noisyFill(MB7T.CAMPFIRE_SIDE, 120, 92, 52, 0.14, rand);
  for (let i = 0; i < 22; i++) { const x = (rand() * 16) | 0, y = (rand() * 16) | 0; px(MB7T.CAMPFIRE_SIDE, x, y, 40, 30, 24); } // char flecks
  for (let x = 5; x <= 10; x++) for (let y = 6; y <= 9; y++) px(MB7T.CAMPFIRE_SIDE, x, y, 158, 128, 82); // cut-log face
  // Base: cold grey ash.
  noisyFill(MB7T.CAMPFIRE_BASE, 58, 54, 52, 0.24, rand);

  // ── MB.1 pass 8 cauldron (dark iron). ──
  // Side: iron staves with vertical banding.
  noisyFill(MB8T.CAULDRON_SIDE, 78, 80, 84, 0.14, rand);
  for (const x of [3, 8, 12] as const) for (let y = 2; y < TILE_PX; y++) px(MB8T.CAULDRON_SIDE, x, y, 56, 58, 62); // bands
  for (let x = 2; x <= 13; x++) { px(MB8T.CAULDRON_SIDE, x, 2, 60, 62, 66); px(MB8T.CAULDRON_SIDE, x, 3, 96, 98, 102); } // top rim highlight
  // Top (empty): iron rim around a dark hollow interior.
  noisyFill(MB8T.CAULDRON_TOP, 40, 42, 46, 0.18, rand);
  for (let i = 2; i <= 13; i++) { px(MB8T.CAULDRON_TOP, 2, i, 92, 94, 98); px(MB8T.CAULDRON_TOP, 13, i, 92, 94, 98); px(MB8T.CAULDRON_TOP, i, 2, 92, 94, 98); px(MB8T.CAULDRON_TOP, i, 13, 92, 94, 98); }

  // ── MB.1 polished andesite / diorite / granite (smoother = low-noise base tint
  //    with a faint diagonal sheen). ──
  const polished = (t: number, [r, g, b]: [number, number, number], seed: number): void => {
    rand = mulberry32(seed);
    noisyFill(t, r, g, b, 0.05, rand);
    for (let y = 0; y < TILE_PX; y++) for (let x = 0; x < TILE_PX; x++) if (((x + y) & 7) === 0) px(t, x, y, r * 1.08, g * 1.08, b * 1.08);
  };
  polished(MB9T.POLISHED_ANDESITE, [138, 138, 142], 9101);
  polished(MB9T.POLISHED_DIORITE, [210, 210, 216], 9102);
  polished(MB9T.POLISHED_GRANITE, [172, 116, 96], 9103);

  // ── M34 Texture Forge: paint the reserved forge tile with the default params.
  //    The client re-skins this tile in place when a forged skin is applied. ──
  ctx.putImageData(forgeImageData(DEFAULT_FORGE_PARAMS), FORGE.SKIN * TILE_PX, 0);

  // ── M35 Script Block: a command-block-style face — a mottled slate base with a
  //    bright "play" chevron marking it as the executable command-block successor. ──
  rand = mulberry32(3501);
  noisyFill(SCRIPT_TILE.BLOCK, 78, 84, 120, 0.16, rand); // dark slate-blue casing
  for (let x = 0; x < TILE_PX; x++) { px(SCRIPT_TILE.BLOCK, x, 0, 150, 158, 210); px(SCRIPT_TILE.BLOCK, x, TILE_PX - 1, 44, 48, 74); } // top highlight / bottom shade
  for (let y = 0; y < TILE_PX; y++) { px(SCRIPT_TILE.BLOCK, 0, y, 60, 64, 96); px(SCRIPT_TILE.BLOCK, TILE_PX - 1, y, 60, 64, 96); } // side bevels
  for (let y = 3; y < TILE_PX - 3; y++) { // a right-pointing chevron (▶) in cyan
    const half = Math.min(y - 3, TILE_PX - 4 - y);
    if (half < 0) continue;
    for (let k = 0; k <= half; k++) px(SCRIPT_TILE.BLOCK, 5 + k, y, 120, 236, 236);
  }

  return cvs;
}

export function makeAtlasTexture(): { texture: THREE.Texture; canvas: HTMLCanvasElement } {
  const canvas = makeAtlasCanvas();
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  return { texture, canvas };
}
